import {
  type CursorPage,
  formatDateTime,
  ListView,
  mapRows,
  PageHeader,
  RecordDrawer,
  TextButton,
  useCursorList,
} from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { JsonViewer } from "@majitap/ui/arc/json-viewer/json-viewer";
import { type InfiniteData, type QueryClient, useMutation, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { api, errorOf, ok } from "../../api";
import { useCompanyFormat } from "../../company";
import { type DeadLetter, developerActions, eventText } from "../../developers";
import { messageFor } from "../../error-copy";
import { RouterLink } from "../../router-link";
import { targetHref } from "../../team";
import styles from "./settings.module.css";

/** A retried event leaves every loaded list: it's due again, not failed. */
export function dropRetried(queryClient: QueryClient, id: string) {
  queryClient.setQueriesData<InfiniteData<CursorPage<DeadLetter>>>({ queryKey: ["dead-letters"] }, (data) =>
    mapRows(data, (rows) => rows.filter((e) => e.id !== id)),
  );
}

/**
 * Failed events (P8-25): events that ran out of retries (an SMS not sent, a sync not made), with
 * the last error and what they carried. Retry sends one again once the cause is fixed.
 */
export function FailedEventsPage(): ReactNode {
  const format = useCompanyFormat();
  const [open, setOpen] = useState<DeadLetter | null>(null);
  const list = useCursorList({
    queryKey: ["dead-letters"],
    fetchPage: (cursor) => ok(api.GET("/v1/admin/outbox/dead-letters", { params: { query: { cursor, limit: 25 } } })),
  });
  return (
    <>
      <PageHeader
        title="Failed events"
        description="Events that ran out of retries. Fix the cause first, then retry: the event runs again in full."
      />
      <ListView
        caption="Failed events"
        itemName={{ one: "event", other: "events" }}
        rows={list.rows}
        rowKey={(e) => e.id}
        status={list.status}
        errorMessage={list.error ? messageFor(errorOf(list.error)) : undefined}
        onRetry={list.refetch}
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        onLoadMore={list.loadMore}
        filtered={false}
        empty={{ title: "Nothing failed", description: "Every event ran." }}
        columns={[
          {
            key: "event",
            label: "Event",
            render: (e) => <TextButton onClick={() => setOpen(e)}>{eventText(e)}</TextButton>,
          },
          {
            key: "last_error",
            label: "Last error",
            render: (e) => <span className={styles.danger}>{e.last_error ?? "No error recorded"}</span>,
          },
          { key: "attempts", label: "Tries", render: (e) => e.attempts },
          {
            key: "created_at",
            label: "Raised",
            render: (e) => <span className={styles.muted}>{formatDateTime(e.created_at, format)}</span>,
          },
          { key: "actions", label: "", render: (e) => <RetryButton event={e} /> },
        ]}
      />
      {open ? (
        <RecordDrawer
          open
          onOpenChange={(o) => !o && setOpen(null)}
          title={open.event_type}
          description={`Raised ${formatDateTime(open.created_at, format)}; last due ${formatDateTime(open.available_at, format)}`}
        >
          {targetHref(open.aggregate_type, open.aggregate_id) ? (
            <RouterLink href={targetHref(open.aggregate_type, open.aggregate_id) ?? ""} className={styles.link}>
              Open the {open.aggregate_type.replace(/_/g, " ")}
            </RouterLink>
          ) : (
            <p className={styles.muted}>
              {open.aggregate_type.replace(/_/g, " ")} {open.aggregate_id}
            </p>
          )}
          {open.last_error ? (
            <Alert tone="danger" title={`Failed after ${open.attempts} tries`}>
              {open.last_error}
            </Alert>
          ) : null}
          <JsonViewer
            data={open.payload}
            rootName="payload"
            defaultExpandDepth={2}
            maxHeight={420}
            label="What it carried"
          />
          <RetryButton event={open} onDone={() => setOpen(null)} />
        </RecordDrawer>
      ) : null}
    </>
  );
}

function RetryButton({ event, onDone }: { event: DeadLetter; onDone?: () => void }) {
  const queryClient = useQueryClient();
  const retry = useMutation({
    mutationFn: () => developerActions.retryEvent(event.id),
    onSuccess: () => {
      dropRetried(queryClient, event.id);
      onDone?.();
    },
  });
  return (
    <span className={styles.stack}>
      <TextButton disabled={retry.isPending} onClick={() => retry.mutate()}>
        {retry.isPending ? "Retrying…" : "Retry"}
      </TextButton>
      {retry.error ? <span className={styles.danger}>{messageFor(errorOf(retry.error))}</span> : null}
    </span>
  );
}
